import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Step,
  StepButton,
  Stepper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import * as React from "react";
import { useSnackbar } from "notistack";
import useBackend from "../../../hooks/use_backend";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { ProbeResults, ProbeStatusBar } from "./probe_results";
import { ProbeSettings } from "./probe_settings";
import { ProbeDeviceAdd, AddableResultSelect } from "./probe_device_add";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../../../enums/react_query_key_enum";
import { fetchSiteData } from "../../../scripts/api_v2/site_client";
import { addDeviceRequest, probeDevice } from "../../../scripts/api_v2/commands_client";
import Snackbar from "../../../scripts/snackbar";
import { Protocol } from "../../../model/device";
import Loader from "../../general/loader";
import { addGPC } from "../../../scripts/api_v2/gpcs_client";
import { useState } from "react";
import { isBlockedForCommissioning } from "../../../scripts/utils";
import { TOOLTIPS_EN } from "../../../enums/tooltip_enum_EN";
import { Tooltip } from "@mui/material";

//import { PROBE_RESULT } from "./test2";

const ButtonNames = Object.freeze({
  SETTINGS: "Settings",
  RETRY: "Retry probe",
  CANCEL: "Cancel",
  PROBE: "Probe device",
  DISCOVERED: "Discovered device(s)",
  ADD: "Add discovered device",
  RESULTS: "See results",
});

const Buttons = ({ lName, rName, lFunc, rFunc, lSx, rSx, rDis = false, lDis = false }) => {
  return (
    <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
      <Button onClick={lFunc} sx={{ mr: 1, ...lSx }} disabled={lDis}>
        {lName}
      </Button>
      <Box sx={{ flex: "1 1 auto" }} />
      <Tooltip title={rDis ? TOOLTIPS_EN.UPDATES_BLOCKED_DURING_COMMISSIONING : ""}>
        <span>
          {" "}
          {/* https://mui.com/material-ui/react-tooltip/#disabled-elements*/}
          <Button onClick={rFunc} sx={{ ...rSx }} disabled={rDis}>
            {rName}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
};

const SettingsStep = ({ settings, setSettings, handleClose, handleProbe }) => {
  return (
    <>
      <Grid mt={3} container justifyContent="start">
        <ProbeSettings settings={settings} setSettings={setSettings} />
      </Grid>
      <Buttons
        lName={ButtonNames.CANCEL}
        rName={ButtonNames.PROBE}
        lFunc={handleClose}
        rFunc={handleProbe}
        lSx={{ color: "red" }}
      />
    </>
  );
};

const ResultsStep = ({ fetching, results, settings, prevStep, nextStep, handleProbe }) => {
  const success = React.useMemo(() => {
    if (results) return Object.values(results).reduce((acc, val) => acc || val.success, false);
  }, [Object.values(results || {})]);
  const onlyExisting = React.useMemo(() => {
    if (results) return Object.values(results).reduce((acc, val) => acc && val.existing_device, true);
  }, [Object.values(results || {})]);
  const rName = !success ? ButtonNames.RETRY : ButtonNames.DISCOVERED;
  const rFunc = !success ? handleProbe : nextStep;

  return (
    <>
      <Grid mt={3} container justifyContent="start">
        <ProbeResults fetching={fetching} results={Object.values(results || {})} settings={settings} />
      </Grid>
      <Buttons
        lName={ButtonNames.SETTINGS}
        rName={rName}
        lFunc={prevStep}
        rFunc={rFunc}
        lSx={{ color: "grey" }}
        rDis={rName === ButtonNames.DISCOVERED && (!success || onlyExisting)}
      />
    </>
  );
};

const DeviceAddStep = ({
  device,
  setDevice,
  addableDict,
  prevStep,
  handleAdd,
  selResult,
  setSelResult,
  isFetching,
  newGPCPrice,
  setNewGPCPrice,
  isBlockedForCommissioning = false,
}) => {
  React.useEffect(() => {
    if (selResult === "" || !(selResult in addableDict)) return;
    setDevice(addableDict[selResult].device);
  }, [selResult]);

  React.useEffect(() => {
    if (Object.keys(addableDict).length === 0) return;
    if (selResult === "" || !(selResult in Object.keys(addableDict))) {
      setSelResult(Object.keys(addableDict)[0]);
    }
  }, [addableDict]);

  if (isFetching)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <Loader fill />
      </Box>
    );
  return (
    <>
      <Grid mt={3} container justifyContent="start">
        {Object.keys(addableDict).length > 0 && (
          <AddableResultSelect addableResults={addableDict} selResult={selResult} setSelResult={setSelResult} />
        )}
        <ProbeDeviceAdd
          device={device}
          setDevice={setDevice}
          existing={selResult.existing_device}
          newGPCPrice={newGPCPrice}
          setNewGPCPrice={setNewGPCPrice}
        />
      </Grid>
      <Buttons
        lName={ButtonNames.RESULTS}
        rName={ButtonNames.ADD}
        lFunc={prevStep}
        rFunc={handleAdd}
        rDis={isBlockedForCommissioning}
        lSx={{ color: "grey" }}
      />
    </>
  );
};

export function ProbeDeviceModal({ open, setOpen, autoProbeResult, setAutoProbeResult }) {
  const steps = ["Settings", "Results", "Device"];
  const stepIdMap = { Settings: 0, Results: 1, Device: 2 };

  const defaultParams = {
    device_class: "battery",
    device_type: "",
    protocol: Protocol.RS485,
    address: "",
    port: null,
    slave: null,
    parity: null,
    baudrate: null,
    stopbits: null,
  };

  const initActiveStep = (autoProbeResult) => {
    if (!autoProbeResult) return stepIdMap["Settings"];
    else if (autoProbeResult.success) return stepIdMap["Device"];
    else return stepIdMap["Results"];
  };

  const addable = (result) => {
    if (!result.success) return false;
    else if (result.existing_device) return false;
    else return true;
  };

  const getAddableDict = (results) => {
    const dict = {};
    if (!results) return dict;
    Object.entries(results).map(([key, val]) => {
      if (addable(val)) {
        dict[key] = val;
      }
    });
    return dict;
  };

  const params = useParams();
  const backend = useBackend();
  let {
    data: siteData,
    error,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
    queryFn: () => fetchSiteData(backend.fetch, params.site_id),
    enabled: false, // sitePage will refresh the query
  });

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [activeStep, setActiveStep] = React.useState(0);
  const [probeParams, setProbeParams] = React.useState(defaultParams);

  const [probeResult, setProbeResult] = React.useState(null);
  const [device, setDevice] = React.useState(null);
  const [addableDict, setAddableDict] = React.useState(getAddableDict(probeResult));
  const [selResult, setSelResult] = React.useState("");
  const [newGPCPrice, setNewGPCPrice] = useState("");

  React.useEffect(() => {
    if (!autoProbeResult) return;
    setActiveStep(initActiveStep(autoProbeResult));
    setProbeResult({ 0: autoProbeResult });
    setProbeParams(autoProbeResult.device);
    setDevice(autoProbeResult.device);
    setAddableDict({ 0: autoProbeResult });
  }, [autoProbeResult]);

  const clearData = () => {
    setDevice(null);
    setProbeResult(null);
    setAutoProbeResult(null);
  };

  function handleClose() {
    setOpen(false);
    clearData();
    setActiveStep(0);
  }

  const nextStep = () => {
    setActiveStep((prev) => prev + 1);
  };

  const previousStep = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const { mutate: probeDeviceMutation, isPending: isProbeMutating } = useMutation({
    mutationFn: async () => {
      clearData();
      setActiveStep(stepIdMap["Results"]);
      return await probeDevice(backend.fetch, params.site_id, probeParams);
    },
    onError: (err, variables, context) => {
      clearData();
      Snackbar.error("Could not probe device: " + err);
    },
    onSuccess: (data) => {
      const dataDict = { ...data };
      setProbeResult(dataDict);
      const addableDict = getAddableDict(dataDict);
      setAddableDict(addableDict);
      if (Object.keys(addableDict).length > 0) setSelResult(Object.keys(addableDict)[0]);
      else setSelResult("");
    },
  });

  const { mutate: addDeviceMutation, isPending: isAddDeviceMutating } = useMutation({
    mutationFn: async () => {
      return await addDeviceRequest(backend.fetch, params.site_id, device);
    },
    onMutate: async (data) => {
      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id] });
      // Ideally we would optimistically update to the new value
    },
    onError: (err, variables, context) => {
      enqueueSnackbar("Device add failed", { variant: "error" });
    },
    onSuccess: async (data) => {
      queryClient.setQueryData([REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id], (prev) => {
        const updated = { ...prev.devices };
        updated[data.device_id] = data;
        return { ...prev, devices: updated };
      });
      enqueueSnackbar("Device added!", { variant: "success" });
      const { [selResult]: _, ...newProbeResult } = probeResult;
      setProbeResult(newProbeResult);
      const addables = getAddableDict(newProbeResult);
      setAddableDict(addables);
      if (Object.keys(addables).length === 0) {
        handleClose();
      }
      // Ideally we would refetch here instead of combining a post and get in one call
      //await refetch()
    },
  });

  async function handleAdd(GPCPrice) {
    const inverter_power = Number(device.inverter_power);
    const capacity = Number(device.capacity);
    const charge_rate = Number(device.charge_rate);
    const price = Number(GPCPrice);

    if (probeResult.existing_device) {
      return enqueueSnackbar("Device already exists in devices", { variant: "warning" });
    } else if (device.name === null || device.name === undefined || device.name === "") {
      return enqueueSnackbar("Give the device a valid name", { variant: "error" });
    } else if (
      device.device_class === "battery" &&
      (device.capacity === null ||
        device.capacity === undefined ||
        device.capacity === 0 ||
        isNaN(capacity) ||
        !Number.isInteger(capacity))
    ) {
      return enqueueSnackbar("Give the battery a valid capacity", { variant: "error" });
    } else if (
      device.device_class === "battery" &&
      (device.charge_rate === null ||
        device.charge_rate === undefined ||
        device.charge_rate === 0 ||
        isNaN(charge_rate) ||
        !Number.isInteger(charge_rate))
    ) {
      return enqueueSnackbar("Give the battery a valid charge rate", { variant: "error" });
    } else if (
      device.device_class === "battery" &&
      device.minimum_soc !== null &&
      device.minimum_soc !== undefined &&
      device.minimum_soc !== "" &&
      (isNaN(Number(device.minimum_soc)) || Number(device.minimum_soc) < 0 || Number(device.minimum_soc) > 100)
    ) {
      return enqueueSnackbar("Minimum SOC must be between 0 and 100%", { variant: "error" });
    } else if (
      device.device_class === "battery" &&
      device.maximum_soc !== null &&
      device.maximum_soc !== undefined &&
      device.maximum_soc !== "" &&
      (isNaN(Number(device.maximum_soc)) || Number(device.maximum_soc) < 0 || Number(device.maximum_soc) > 100)
    ) {
      return enqueueSnackbar("Maximum SOC must be between 0 and 100%", { variant: "error" });
    } else if (
      device.device_class === "battery" &&
      device.minimum_soc !== null &&
      device.minimum_soc !== undefined &&
      device.minimum_soc !== "" &&
      device.maximum_soc !== null &&
      device.maximum_soc !== undefined &&
      device.maximum_soc !== "" &&
      Number(device.minimum_soc) > Number(device.maximum_soc)
    ) {
      return enqueueSnackbar("Minimum SOC cannot be greater than Maximum SOC", { variant: "error" });
    } else if (
      device.device_class === "solar_inverter" &&
      (device.inverter_power === null ||
        device.inverter_power === undefined ||
        device.inverter_power === 0 ||
        isNaN(inverter_power) ||
        !Number.isInteger(inverter_power))
    ) {
      return enqueueSnackbar("Give the inverter a valid inverter power", { variant: "error" });
    } else if (
      device.device_class === "solar_inverter" &&
      (GPCPrice === null ||
        GPCPrice === undefined ||
        GPCPrice === "" ||
        price < 0 ||
        isNaN(price) ||
        !Number.isInteger(price))
    ) {
      return enqueueSnackbar("Give the inverter a valid Green power certificate (0 if nonexistent)", {
        variant: "error",
      });
    }
    addDeviceMutation();

    if (device.device_class === "solar_inverter") {
      try {
        await addGPC(backend.fetch, params.site_id, {
          device_id: device.device_id,
          price: price,
          previous: null,
        });
        enqueueSnackbar("Green power certificate saved", { variant: "success" });
      } catch (err) {
        enqueueSnackbar(err?.message ?? String(err), { variant: "error" });
      }
    }
  }

  async function handleProbe() {
    if (probeParams.device_type === "") {
      return enqueueSnackbar("Select a device type", { variant: "error" });
    }
    probeDeviceMutation();
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="probe-dialog-title"
      aria-describedby="probe-dialog-description"
      fullWidth
      maxWidth={"md"}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <span>Probe device</span>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep}>
          {steps.map((label, index) => (
            <Step key={label} completed={index < activeStep}>
              <StepButton
                color="inherit"
                onClick={() => {
                  setActiveStep(index);
                }}
              >
                {label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
        {activeStep === 0 && (
          <SettingsStep
            settings={probeParams}
            setSettings={setProbeParams}
            handleClose={handleClose}
            handleProbe={handleProbe}
          />
        )}
        {activeStep === 1 && (
          <ResultsStep
            fetching={isProbeMutating}
            results={probeResult}
            settings={probeParams}
            prevStep={previousStep}
            nextStep={nextStep}
            handleProbe={handleProbe}
          />
        )}
        {activeStep === 2 && (
          <DeviceAddStep
            device={device}
            setDevice={setDevice}
            results={probeResult}
            prevStep={previousStep}
            isBlockedForCommissioning={isBlockedForCommissioning(siteData)}
            handleAdd={() => handleAdd(newGPCPrice)}
            selResult={selResult}
            setSelResult={setSelResult}
            addableDict={addableDict}
            isFetching={isAddDeviceMutating}
            newGPCPrice={newGPCPrice}
            setNewGPCPrice={setNewGPCPrice}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
